package com.cybersoftsolution.cybersoftsolutionweb.components.buttons

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.cybersoftsolution.cybersoftsolutionweb.components.icons.MyIco
import com.cybersoftsolution.cybersoftsolutionweb.models.Theme
import com.cybersoftsolution.cybersoftsolutionweb.utils.IdGenerator
import com.cybersoftsolution.cybersoftsolutionweb.utils.Resources
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.Gradient
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun MyLoadingButton(
    modifier: Modifier = Modifier,
    id: String? = null,
    text: String,
    variant: MyButtonVariant = MyButtonVariant.Primary,
    type: ButtonType = ButtonType.Button,
    size: ButtonSize = ButtonSize.Default,
    borderRadius: MyBorderRadius? = null,
    customization: MyButtonCustomization? = null,
    disabled: Boolean = false,
    loading: Boolean = false,
    loadingText: String? = "Procesando ...",
    badge: ButtonBadge? = null,
    icon: String? = null,
    image: (@Composable () -> Unit)? = null,
    onClick: () -> Unit
) {
    val randomId = remember {
        id ?: IdGenerator.generateUniqueId("button")
    }
    Button(attrs = Modifier
        .then(modifier)
        .id(randomId)
        .onClick { onClick() }
        .thenIf(
            condition = customization == null && borderRadius != null,
            other = borderRadius?.let {
                Modifier.borderRadius(
                    topLeft = it.topLeft,
                    topRight = borderRadius.topRight,
                    bottomRight = borderRadius.bottomRight,
                    bottomLeft = borderRadius.bottomLeft
                )
            } ?: Modifier
        )
        .thenIf(
            condition = customization == null,
            other = Modifier.classNames(
                *variant.classes.toTypedArray(),
                size.value
            )
        )
        .thenIf(
            condition = customization != null,
            other = Modifier.classNames(ButtonSize.Default.value)
        )
//        .classNames(
//            "d-flex",
//            "align-items-center",
//        )
        .thenIf(
            condition = badge != null,
            other = Modifier.classNames("position-relative")
        )
        .styleModifier {
            if (customization != null) {
                customization.color?.let { property(ButtonProperty.COLOR, it) }
                customization.backgroundColor?.let { property(ButtonProperty.BACKGROUND_COLOR, it) }
                customization.borderColor?.let { property(ButtonProperty.BORDER_COLOR, it) }
                customization.hoverColor?.let { property(ButtonProperty.HOVER_COLOR, it) }
                customization.hoverBackgroundColor?.let { property(ButtonProperty.HOVER_BACKGROUND_COLOR, it) }
                customization.hoverBorderColor?.let { property(ButtonProperty.HOVER_BORDER_COLOR, it) }
                customization.activeColor?.let { property(ButtonProperty.ACTIVE_COLOR, it) }
                customization.activeBackgroundColor?.let { property(ButtonProperty.ACTIVE_BACKGROUND_COLOR, it) }
                customization.activeBorderColor?.let { property(ButtonProperty.ACTIVE_BORDER_COLOR, it) }
                customization.disabledColor?.let { property(ButtonProperty.DISABLED_COLOR, it) }
                customization.disabledBackgroundColor?.let {
                    property(
                        ButtonProperty.DISABLED_BACKGROUND_COLOR,
                        it
                    )
                }
                customization.disabledBorderColor?.let { property(ButtonProperty.DISABLED_BORDER_COLOR, it) }

                property(ButtonProperty.BORDER_RADIUS, customization.borderRadius.value)
                property(ButtonProperty.HORIZONTAL_PADDING, customization.horizontalPadding)
                property(ButtonProperty.VERTICAL_PADDING, customization.verticalPadding)

                customization.fontFamily?.let { property(ButtonProperty.FONT_FAMILY, it) }
                property(ButtonProperty.FONT_SIZE, customization.fontSize)
                property(ButtonProperty.FONT_WEIGHT, customization.fontWeight.toString())
                property(ButtonProperty.LINE_HEIGHT, customization.lineHeight.toString())

                property(ButtonProperty.GRADIENT, customization.gradient.toString())
            }
        }
        .toAttrs {
            attr("type", type.value)
            if (disabled || loading) disabled()
        }
    ) {
        if (badge != null) {
            MyBadge(
                modifier = badge.modifier
                    .classNames(
                        "position-absolute",
                        "top-0",
                        "start-100",
                        "translate-middle"
                    ),
                text = badge.text,
                style = badge.style,
                variant = badge.variant,
            )
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
            modifier = Modifier
                .fillMaxHeight()
                .fillMaxWidth()
        ) {
            if (loading) {
                if (loadingText != null) {
                    Span(attrs = Modifier
                        .margin(right = 6.px)
                        .classNames("spinner-border", "spinner-border-sm")
                        .toAttrs {
                            attr("role", "status")
                            attr("aria-hidden", "true")
                        }
                    )
                    Text(value = loadingText)
                } else {
                    Span(attrs = Modifier
                        .classNames("spinner-border", "spinner-border-sm")
                        .toAttrs {
                            attr("role", "status")
                            attr("aria-hidden", "true")
                        }
                    )
                    Span(
                        attrs = Modifier
                            .classNames("visually-hidden")
                            .toAttrs()
                    ) {
                        Text(value = "Loading...")
                    }
                }
            } else {
                if (image != null) {
                    image()
                }
                if (icon != null) {
                    MyIco(
                        modifier = Modifier.margin(leftRight = 8.px),
                        icon = icon,
                        size = 24.px
                    )
                    SpanText(text = text)
                } else {
                    SpanText(text = text)
                }
            }
        }

    }
}


data class MyButtonCustomization(
    val color: CSSColorValue? = null,
    val backgroundColor: CSSColorValue? = null,
    val borderColor: CSSColorValue? = null,
    val hoverColor: CSSColorValue? = null,
    val hoverBackgroundColor: CSSColorValue? = null,
    val hoverBorderColor: CSSColorValue? = null,
    val activeColor: CSSColorValue? = null,
    val activeBackgroundColor: CSSColorValue? = null,
    val activeBorderColor: CSSColorValue? = null,
    val disabledColor: CSSColorValue? = null,
    val disabledBackgroundColor: CSSColorValue? = null,
    val disabledBorderColor: CSSColorValue? = null,

    val borderRadius: MyBorderRadius = MyBorderRadius(all = 0.px),
    val horizontalPadding: CSSNumeric = 0.75.cssRem,
    val verticalPadding: CSSNumeric = 0.375.cssRem,

    val fontFamily: String? = null,
    val fontSize: CSSNumeric = 1.cssRem,
    val fontWeight: FontWeight = FontWeight.Normal,
    val lineHeight: CSSNumeric = 1.5.cssRem,

    val gradient: Gradient? = null
)

data class MyBorderRadius(
    val topLeft: CSSLengthOrPercentageNumericValue = 0.px,
    val topRight: CSSLengthOrPercentageNumericValue = 0.px,
    val bottomRight: CSSLengthOrPercentageNumericValue = 0.px,
    val bottomLeft: CSSLengthOrPercentageNumericValue = 0.px
) {
    constructor(all: CSSLengthOrPercentageNumericValue = 0.px) : this(
        topLeft = all,
        topRight = all,
        bottomRight = all,
        bottomLeft = all
    )

    val value = "$topLeft $topRight $bottomRight $bottomLeft"
}

data class ButtonBadge(
    val modifier: Modifier = Modifier,
    val text: String,
    val style: BackgroundStyle = BackgroundStyle.Danger,
    val variant: BadgeVariant = BadgeVariant.Rounded
)

enum class MyButtonVariant(val classes: List<String>) {
    Primary(classes = listOf("btn", "btn-primary")),
    PrimaryOutline(classes = listOf("btn", "btn-outline-primary")),
    Secondary(classes = listOf("btn", "btn-secondary")),
    SecondaryOutline(classes = listOf("btn", "btn-outline-secondary")),
    Success(classes = listOf("btn", "btn-success")),
    SuccessOutline(classes = listOf("btn", "btn-outline-success")),
    Danger(classes = listOf("btn", "btn-danger")),
    DangerOutline(classes = listOf("btn", "btn-outline-danger")),
    Warning(classes = listOf("btn", "btn-warning")),
    WarningOutline(classes = listOf("btn", "btn-outline-warning")),
    Info(classes = listOf("btn", "btn-info")),
    InfoOutline(classes = listOf("btn", "btn-outline-info")),
    Light(classes = listOf("btn", "btn-light")),
    LightOutline(classes = listOf("btn", "btn-outline-light")),
    Dark(classes = listOf("btn", "btn-dark")),
    DarkOutline(classes = listOf("btn", "btn-outline-dark")),
    Link(classes = listOf("btn", "btn-link")),
    LinkOutline(classes = listOf("btn", "btn-outline-link")),
}


enum class ButtonType(val value: String) {
    Button(value = "button"),
    Submit(value = "submit"),
    Reset(value = "reset")
}

enum class ButtonSize(val value: String) {
    Default(value = "btn"),
    Small(value = "btn-sm"),
    Large(value = "btn-lg")
}

object ButtonProperty {
    const val COLOR = "--bs-btn-color"
    const val BACKGROUND_COLOR = "--bs-btn-bg"
    const val BORDER_COLOR = "--bs-btn-border-color"
    const val HOVER_COLOR = "--bs-btn-hover-color"
    const val HOVER_BACKGROUND_COLOR = "--bs-btn-hover-bg"
    const val HOVER_BORDER_COLOR = "--bs-btn-hover-border-color"
    const val ACTIVE_COLOR = "--bs-btn-active-color"
    const val ACTIVE_BACKGROUND_COLOR = "--bs-btn-active-bg"
    const val ACTIVE_BORDER_COLOR = "--bs-btn-active-border-color"
    const val DISABLED_COLOR = "--bs-btn-disabled-color"
    const val DISABLED_BACKGROUND_COLOR = "--bs-btn-disabled-bg"
    const val DISABLED_BORDER_COLOR = "--bs-btn-disabled-border-color"

    const val BORDER_RADIUS = "--bs-btn-border-radius"
    const val HORIZONTAL_PADDING = "--bs-btn-padding-x"
    const val VERTICAL_PADDING = "--bs-btn-padding-y"

    const val FONT_FAMILY = "--bs-btn-font-family"
    const val FONT_SIZE = "--bs-btn-font-size"
    const val FONT_WEIGHT = "--bs-btn-font-weight"
    const val LINE_HEIGHT = "--bs-btn-line-height"

    const val GRADIENT = "background"
}


@Composable
fun MyBadge(
    modifier: Modifier = Modifier,
    text: String,
    fontFamily: String? = null,
    fontSize: CSSLengthOrPercentageNumericValue? = null,
    fontWeight: FontWeight? = null,
    style: BackgroundStyle = BackgroundStyle.Primary,
    variant: BadgeVariant = BadgeVariant.Regular
) {
    Span(
        attrs = modifier
            .classNames(style.value)
            .thenIf(
                condition = variant != BadgeVariant.Regular && variant != BadgeVariant.Straight,
                other = variant.classes?.let { Modifier.classNames(*it.toTypedArray()) } ?: Modifier
            )
            .thenIf(
                condition = variant != BadgeVariant.Empty,
                other = Modifier.classNames("badge")
            )
            .thenIf(
                condition = variant == BadgeVariant.Straight,
                other = Modifier.borderRadius(0.px)
            )
            .toAttrs()
    ) {
        if (variant != BadgeVariant.Empty) {
            SpanText(
                modifier = Modifier
                    .thenIf(
                        condition = fontFamily != null,
                        other = fontFamily?.let { Modifier.fontFamily(it) } ?: Modifier
                    )
                    .thenIf(
                        condition = fontSize != null,
                        other = fontSize?.let { Modifier.fontSize(it) } ?: Modifier
                    )
                    .thenIf(
                        condition = fontWeight != null,
                        other = fontWeight?.let { Modifier.fontWeight(it) } ?: Modifier
                    ),
                text = text
            )
        }
    }
}

enum class BackgroundStyle(val value: String) {
    Primary(value = "bg-primary"),
    Secondary(value = "bg-secondary"),
    Success(value = "bg-success"),
    Danger(value = "bg-danger"),
    Warning(value = "bg-warning"),
    Info(value = "bg-info"),
    Light(value = "bg-light"),
    Dark(value = "bg-dark")
}
enum class BadgeVariant(val classes: List<String>? = null) {
    Regular,
    Straight,
    Rounded(listOf("rounded-pill")),
    Empty(listOf("border", "border-light", "rounded-circle", "p-2"))
}